import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, RefreshCw, Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API, STATI, URGENZE } from "@/lib/config";
import { AdminLogin } from "@/components/gestione/AdminLogin";
import { StatsBar } from "@/components/gestione/StatsBar";
import { InterventoCard } from "@/components/gestione/InterventoCard";
import { DettaglioDialog } from "@/components/gestione/DettaglioDialog";
import { RecensioniAdmin } from "@/components/gestione/RecensioniAdmin";
import { NuovoLinkRecensione } from "@/components/gestione/NuovoLinkRecensione";

const Filtro = ({ label, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3 h-9 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"}`}>{label}</button>
);

export default function Gestione() {
  const location = useLocation();
  const [pwd, setPwd] = useState(() => sessionStorage.getItem("idro_admin") || "");
  const [gUser, setGUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!pwd && !location.state?.user && !location.state?.authError);
  const authed = !!(pwd || gUser);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [stato, setStato] = useState("");
  const [urgenza, setUrgenza] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [vista, setVista] = useState("interventi");

  const headers = { "X-Admin-Password": pwd };

  const carica = useCallback(async () => {
    try {
      const params = { ...(stato && { stato }), ...(urgenza && { urgenza }), ...(q && { q }) };
      const [l, s] = await Promise.all([
        axios.get(`${API}/interventi`, { headers: { "X-Admin-Password": pwd }, params }),
        axios.get(`${API}/interventi/stats`, { headers: { "X-Admin-Password": pwd } }),
      ]);
      setItems(l.data); setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) { sessionStorage.removeItem("idro_admin"); setPwd(""); setGUser(null); }
    }
  }, [pwd, stato, urgenza, q]);

  useEffect(() => {
    if (location.state?.authError) window.history.replaceState({}, "");
  }, [location.state]);

  useEffect(() => {
    if (!checking) return;
    axios.get(`${API}/auth/me`).then((r) => setGUser(r.data)).catch(() => {}).finally(() => setChecking(false));
  }, [checking]);

  useEffect(() => { if (authed) { const t = setTimeout(carica, 250); return () => clearTimeout(t); } }, [authed, carica]);

  const aggiorna = async (id, data) => {
    const prima = items.find((i) => i.id === id);
    const r = await axios.patch(`${API}/interventi/${id}`, data, { headers });
    setSel((s) => (s?.id === id ? r.data : s));
    toast.success("Intervento aggiornato");
    if (data.stato === "completato" && !r.data.recensito) {
      if (r.data.promemoria_recensione_at && !prima?.promemoria_recensione_at) toast.success(`Link recensione inviato via email a ${r.data.email}`);
      else if (!r.data.email) toast.info("Il cliente non ha un'email: inviagli il link recensione su WhatsApp dal dettaglio");
    }
    carica();
  };

  const elimina = async (id) => {
    await axios.delete(`${API}/interventi/${id}`, { headers });
    setSel(null); toast.success("Intervento eliminato"); carica();
  };

  const esci = async () => {
    sessionStorage.removeItem("idro_admin"); setPwd("");
    await axios.post(`${API}/auth/logout`).catch(() => {}); setGUser(null);
  };

  if (checking) return <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="gestione-checking"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;
  if (!authed) return <AdminLogin authError={location.state?.authError} onLogin={(u) => setGUser(u)} />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="gestione-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100" aria-label="Torna al sito" data-testid="gestione-back-link"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="text-lg sm:text-xl font-extrabold tracking-tight">Gestione interventi</h1>
          </div>
          <div className="flex gap-2 items-center">
            {gUser && <span className="hidden sm:inline text-sm text-slate-500" data-testid="gestione-user-email">{gUser.email}</span>}
            <button onClick={carica} className="w-9 h-9 grid place-items-center rounded-full border border-slate-200 hover:bg-slate-100" aria-label="Aggiorna" data-testid="gestione-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={esci} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-slate-200 text-sm font-semibold hover:bg-slate-100" data-testid="gestione-logout-btn"><LogOut className="w-4 h-4" /> Esci</button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="inline-flex rounded-full bg-white border border-slate-200 p-1">
          <Filtro label="Interventi" active={vista === "interventi"} onClick={() => setVista("interventi")} testid="tab-interventi" />
          <Filtro label="Recensioni" active={vista === "recensioni"} onClick={() => setVista("recensioni")} testid="tab-recensioni" />
        </div>
        {vista === "recensioni" ? <><NuovoLinkRecensione pwd={pwd} /><RecensioniAdmin pwd={pwd} /></> : <>
        <StatsBar stats={stats} />
        <div className="space-y-3">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, indirizzo, telefono, problema..." className="pl-9 h-11 bg-white" data-testid="gestione-search-input" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Filtro label="Tutti gli stati" active={!stato} onClick={() => setStato("")} testid="filter-stato-tutti" />
            {Object.entries(STATI).map(([k, v]) => <Filtro key={k} label={v.label} active={stato === k} onClick={() => setStato(k)} testid={`filter-stato-${k}`} />)}
          </div>
          <div className="flex flex-wrap gap-2">
            <Filtro label="Tutte le urgenze" active={!urgenza} onClick={() => setUrgenza("")} testid="filter-urgenza-tutte" />
            {Object.entries(URGENZE).map(([k, v]) => <Filtro key={k} label={v.label} active={urgenza === k} onClick={() => setUrgenza(k)} testid={`filter-urgenza-${k}`} />)}
          </div>
        </div>
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="gestione-empty">Nessun intervento trovato.</div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="interventi-list">
            {items.map((it) => <InterventoCard key={it.id} it={it} onOpen={() => setSel(it)} onStato={(s) => aggiorna(it.id, { stato: s })} />)}
          </div>
        )}
        </>}
      </main>
      <DettaglioDialog it={sel} onClose={() => setSel(null)} onSave={aggiorna} onDelete={elimina} />
    </div>
  );
}
